import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';
import DarkModeToggle from './DarkModeToggle';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const cartCount = (() => {
    try {
      const raw = localStorage.getItem('kenstore_cart');
      const cart = raw ? JSON.parse(raw) : [];
      return Array.isArray(cart) ? cart.reduce((sum: number, i: { quantity?: number }) => sum + (i.quantity || 0), 0) : 0;
    } catch {
      return 0;
    }
  })();

  const navTextColor = scrolled ? 'text-foreground-800' : 'text-background-50';
  const navHoverBg = scrolled ? 'hover:bg-background-100' : 'hover:bg-background-50/20';
  const navMutedColor = scrolled ? 'text-foreground-500' : 'text-background-50/60';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false);
      }
      if (megaMenuRef.current && !megaMenuRef.current.contains(e.target as Node)) {
        setMegaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (megaMenuOpen && categories.length > 0) {
      setSelectedCategory(categories[0].id);
    }
  }, [megaMenuOpen]);

  const activeCat = categories.find((c) => c.id === selectedCategory);

  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (q.length > 1) {
      const cats = categories
        .filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
        .map((c) => c.name)
        .slice(0, 4);
      const suggestions = ['Samsung Galaxy S24', 'Nike Air Force', 'Sony Headphones', 'Air Fryer']
        .filter((s) => s.toLowerCase().includes(q.toLowerCase()));
      setSearchResults([...cats, ...suggestions].slice(0, 6));
    } else {
      setSearchResults([]);
    }
  };

  const handleMegaMenuToggle = () => {
    setMegaMenuOpen((prev) => !prev);
    setAccountOpen(false);
    setHelpOpen(false);
  };

  const handleMegaMenuClose = () => {
    setMegaMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/60'
            : 'bg-primary-500'
        }`}
      >
        <div className="w-full px-4 md:px-6 lg:px-8">
          {/* Top bar */}
          <div className="flex items-center justify-between h-14 md:h-16 gap-3">
            {/* Logo */}
            <a href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className={`relative rounded-md overflow-hidden ring-2 transition-all duration-300 ${scrolled ? 'ring-primary-500' : 'ring-white'}`}>
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_db1e94dd529956a9cd4d4afc7c6999ce_1329fca5.jpg"
                  alt="KenStore"
                  className="h-9 md:h-10 w-auto"
                />
              </div>
              <span className={`hidden sm:block text-base md:text-lg font-bold tracking-tight font-[family-name:var(--font-heading)] transition-colors duration-300 ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                kenstore
              </span>
            </a>

            {/* Search Bar - Desktop */}
            <div ref={searchRef} className="hidden md:flex flex-1 max-w-2xl mx-4 relative">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search for products, brands, categories..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setSearchOpen(true)}
                  className="w-full h-10 pl-11 pr-4 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition-all"
                />
                <i className={`ri-search-line absolute left-4 top-1/2 -translate-y-1/2 ${navMutedColor}`} />
                <button className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-7 px-4 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${scrolled ? 'bg-primary-500 text-background-50 hover:bg-primary-600' : 'bg-background-50 text-primary-600 hover:bg-background-100'}`}>
                  Search
                </button>
              </div>
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden z-50">
                  {searchResults.map((r, i) => (
                    <a
                      key={i}
                      href={`/search?q=${encodeURIComponent(r)}`}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-search-line text-foreground-500 text-sm" />
                      <span className="text-sm text-foreground-800">{r}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-1 md:gap-2">
              {/* Mobile search toggle */}
              <button
                type="button"
                onClick={() => setSearchOpen((s) => !s)}
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors ${navTextColor} ${navHoverBg}`}
                aria-label="Search"
              >
                <i className={`ri-search-line text-lg ${navTextColor}`} />
              </button>

              {/* Account */}
              <div ref={accountRef} className="relative hidden sm:block">
                <button
                  type="button"
                  onClick={() => {
                    setAccountOpen((a) => !a);
                    setHelpOpen(false);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors whitespace-nowrap ${navTextColor} ${navHoverBg}`}
                >
                  <i className={`ri-user-line text-lg ${navTextColor}`} />
                  <span className={`text-sm font-medium hidden lg:inline ${navTextColor}`}>Account</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${navMutedColor} ${accountOpen ? 'rotate-180' : ''}`} />
                </button>
                {accountOpen && (
                  <div className="absolute top-full right-0 mt-2 w-56 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden z-50 py-2">
                    <a
                      href="/login"
                      className="block mx-2 px-3 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors"
                    >
                      Sign In
                    </a>
                    <div className="mt-2 border-t border-background-200/60 pt-2">
                      <a
                        href="/account"
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-user-line text-lg text-foreground-600" />
                        <span className="text-sm text-foreground-800">My Account</span>
                      </a>
                      <a
                        href="/account/orders"
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-archive-line text-lg text-foreground-600" />
                        <span className="text-sm text-foreground-800">Orders</span>
                      </a>
                      <a
                        href="/wishlist"
                        className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-heart-line text-lg text-foreground-600" />
                        <span className="text-sm text-foreground-800">Wishlist</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Help */}
              <div ref={helpRef} className="relative hidden md:block">
                <button
                  type="button"
                  onClick={() => {
                    setHelpOpen((h) => !h);
                    setAccountOpen(false);
                  }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors whitespace-nowrap ${navTextColor} ${navHoverBg}`}
                >
                  <i className={`ri-question-line text-lg ${navTextColor}`} />
                  <span className={`text-sm font-medium hidden lg:inline ${navTextColor}`}>Help</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform ${navMutedColor} ${helpOpen ? 'rotate-180' : ''}`} />
                </button>
                {helpOpen && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 overflow-hidden z-50 py-2">
                    <a
                      href="/faq"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-customer-service-2-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Help Center</span>
                    </a>
                    <a
                      href="/how-to-buy"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-shopping-cart-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Place your Order</span>
                    </a>
                    <a
                      href="/payment"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-bank-card-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Payment Options</span>
                    </a>
                    <a
                      href="/shipping"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-truck-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Delivery Timelines & Track your Order</span>
                    </a>
                    <a
                      href="/returns"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-arrow-go-back-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Returns & Refunds</span>
                    </a>
                    <a
                      href="/warranty"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-shield-check-line text-lg text-foreground-600" />
                      <span className="text-sm text-foreground-800">Warranty</span>
                    </a>
                    <div className="border-t border-background-200/60 mx-2 my-2" />
                    <a
                      href="/live-chat"
                      className="flex items-center gap-3 mx-2 px-3 py-2.5 rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
                    >
                      <i className="ri-message-3-line text-lg" />
                      <span className="text-sm font-semibold">Live Chat</span>
                    </a>
                    <a
                      href="https://wa.me/254700000000"
                      rel="nofollow noopener"
                      className="flex items-center gap-3 mx-2 mt-1 px-3 py-2.5 rounded-lg border border-green-500 text-green-600 hover:bg-green-50 transition-colors"
                    >
                      <i className="ri-whatsapp-line text-lg" />
                      <span className="text-sm font-semibold">WhatsApp</span>
                    </a>
                  </div>
                )}
              </div>

              {/* Cart */}
              <a
                href="/cart"
                className={`relative flex items-center gap-1.5 px-3 py-2 rounded-full transition-colors whitespace-nowrap ${navTextColor} ${navHoverBg}`}
              >
                <i className={`ri-shopping-cart-2-line text-lg ${navTextColor}`} />
                {cartCount > 0 && (
                  <span className={`absolute -top-0.5 -right-0.5 w-5 h-5 flex items-center justify-center rounded-full text-xs font-bold ${scrolled ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-primary-600'}`}>
                    {cartCount}
                  </span>
                )}
                <span className={`text-sm font-medium hidden lg:inline ${navTextColor}`}>Cart</span>
              </a>

              {/* Dark Mode */}
              <div className="hidden md:block">
                <DarkModeToggle />
              </div>

              {/* Mobile menu toggle */}
              <button
                type="button"
                onClick={() => setMenuOpen((m) => !m)}
                className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full transition-colors ${navTextColor} ${navHoverBg}`}
                aria-label="Menu"
              >
                <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-lg ${navTextColor}`} />
              </button>
            </div>
          </div>

          {/* Category nav bar */}
          <div ref={megaMenuRef} className="hidden md:block relative -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 bg-background-100 -mb-2 mt-0.5">
            <div className="flex items-center gap-1 py-2">
              <button
                type="button"
                onClick={handleMegaMenuToggle}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  megaMenuOpen
                    ? 'bg-primary-600 text-background-50'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                <i className="ri-apps-line" />
                All Categories
                <i className={`ri-arrow-down-s-line text-sm transition-transform ${megaMenuOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* ===== Mega Menu Sidebar + Panel ===== */}
            {megaMenuOpen && (
              <div className="absolute top-full left-0 right-0 z-40">
                <div className="bg-background-50 border-t border-b border-background-200/70 shadow-xl">
                  <div className="w-full flex min-h-[420px]">
                    {/* Left sidebar — category list */}
                    <div className="w-64 flex-shrink-0 border-r border-background-200/60 bg-background-50 py-3 overflow-y-auto max-h-[520px]">
                      {categories.map((cat) => {
                        const isActive = selectedCategory === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setSelectedCategory(cat.id)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors cursor-pointer ${
                              isActive
                                ? 'bg-primary-50 text-primary-600'
                                : 'text-foreground-800 hover:bg-background-100'
                            }`}
                          >
                            <i className={`${cat.icon} text-lg ${isActive ? 'text-primary-500' : 'text-foreground-500'}`} />
                            <span className={`text-sm font-medium ${isActive ? 'text-primary-600' : 'text-foreground-800'}`}>
                              {cat.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Right panel — subcategory groups */}
                    <div className="flex-1 px-6 py-5 overflow-y-auto max-h-[520px]">
                      {activeCat ? (
                        <div className="grid grid-cols-3 gap-x-8 gap-y-6">
                          {(activeCat.subcategoryGroups || []).map((group) => (
                            <div key={group.title}>
                              <a
                                href={`/shop/${activeCat.slug}`}
                                onClick={handleMegaMenuClose}
                                className="block text-sm font-bold text-foreground-950 uppercase tracking-wide mb-2 hover:text-primary-600 transition-colors"
                              >
                                {group.title}
                              </a>
                              <div className="border-t border-background-200/70 pt-2 space-y-1.5">
                                {group.items.map((item) => (
                                  <a
                                    key={item.slug}
                                    href={`/shop/${activeCat.slug}/${item.slug}`}
                                    onClick={handleMegaMenuClose}
                                    className="block text-sm text-foreground-600 hover:text-foreground-900 hover:underline transition-colors"
                                  >
                                    {item.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-full text-foreground-500 text-sm">
                          Select a category to explore
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Search Overlay */}
      {searchOpen && (
        <div className="md:hidden fixed top-14 left-0 right-0 z-40 bg-background-50 border-b border-background-200/70 p-3 shadow-lg">
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              autoFocus
              className="w-full h-10 pl-10 pr-4 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500" />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 bg-background-50 rounded-lg overflow-hidden">
              {searchResults.map((r, i) => (
                <a
                  key={i}
                  href={`/search?q=${encodeURIComponent(r)}`}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-background-100 transition-colors"
                >
                  <i className="ri-search-line text-foreground-500 text-sm" />
                  <span className="text-sm text-foreground-800">{r}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-background-50 pt-16 overflow-y-auto">
          <div className="p-4 space-y-1">
            <div className="pb-3 mb-3 border-b border-background-200/60">
              <a
                href="/login"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-user-line text-xl text-primary-500" />
                <span className="text-base font-medium text-foreground-900">My Account</span>
              </a>
              <a
                href="/cart"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-shopping-cart-2-line text-xl text-primary-500" />
                <span className="text-base font-medium text-foreground-900">My Cart</span>
              </a>
              <a
                href="/wishlist"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-heart-line text-xl text-primary-500" />
                <span className="text-base font-medium text-foreground-900">Wishlist</span>
              </a>
              <a
                href="/track-order"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-truck-line text-xl text-primary-500" />
                <span className="text-base font-medium text-foreground-900">Track Order</span>
              </a>
            </div>
            <p className="px-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider">Categories</p>
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={`/shop/${cat.slug}`}
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className={`${cat.icon} text-lg text-primary-500`} />
                <span className="text-sm font-medium text-foreground-800">{cat.name}</span>
                <i className="ri-arrow-right-s-line ml-auto text-foreground-500" />
              </a>
            ))}
            <div className="pt-3 mt-3 border-t border-background-200/60">
              <a
                href="/flash-deals"
                className="flex items-center gap-3 px-3 py-3 rounded-lg bg-primary-100 hover:bg-primary-200 transition-colors"
              >
                <i className="ri-flashlight-line text-lg text-primary-600" />
                <span className="text-sm font-medium text-primary-700">Flash Deals</span>
              </a>
              <a
                href="/become-seller"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-store-line text-lg text-accent-600" />
                <span className="text-sm font-medium text-foreground-800">Sell on KenStore</span>
              </a>
              <a
                href="/faq"
                className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
              >
                <i className="ri-customer-service-line text-lg text-secondary-600" />
                <span className="text-sm font-medium text-foreground-800">Help Center</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}